// src/data/garde-meubles-usages.ts
//
// Garde-meubles : trois pages cas d'usage — LOT 8 du plan SEO.
//
// Trois situations de vie (étudiant, entre deux logements, entreprise et
// archives), pas trois habillages d'un même texte : la même exigence de
// différenciation que le lot 7, et le même contrôle 12h qui mesure la
// similarité entre les pages servies. Les faits communs — box à Seraing,
// grille, durée minimum, assurance, magasinier — viennent du registre et du
// catalogue, une fois, dans le gabarit ; la donnée ne porte que la situation.
//
// Les volumes cités sont des PALIERS de la grille du registre : le validateur
// refuse un « 5 m³ » qui n'existe pas dans la grille.
import donnees from './garde-meubles-usages.json';
import { valeurDe } from './registre';
import { RACINE_GARDE_MEUBLES } from './garde-meubles-communes';

export type SituationUsage = 'etudiant' | 'entre-deux-logements' | 'entreprise';

export interface PageGardeMeublesUsage {
  slug: string;
  situation: SituationUsage;
  publiable: boolean;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  requetes: string[];
  titreMeta: string;
  descriptionMeta: string;
  h1: string;
  capsule: string;
  /** La page de service déménagement correspondante (maillage imposé par le plan). */
  servicePage: string;
  /** Les paliers de la grille auxquels la page renvoie. */
  volumesCites: number[];
  sections: Array<{ titre: string; texte: string }>;
  aRetenir: string;
  faq: Array<{ q: string; a: string }>;
  aValider: string[];
}

interface Donnees {
  pages: PageGardeMeublesUsage[];
}

const D = donnees as unknown as Donnees;

export const PAGES_USAGES: readonly PageGardeMeublesUsage[] = D.pages;

export function cheminUsage(slug: string): string {
  return `${RACINE_GARDE_MEUBLES}/${slug}`;
}

export function usageParChemin(chemin: string): PageGardeMeublesUsage | undefined {
  return PAGES_USAGES.find((p) => cheminUsage(p.slug) === chemin);
}

export function usageVerifie(p: PageGardeMeublesUsage): boolean {
  return p.verifiePar === 'human' && Boolean(p.dateVerification);
}

export interface ProblemeUsage {
  ou: string;
  gravite: 'erreur' | 'avertissement';
  message: string;
}

/** Les paliers de la grille tarifaire, en m³ ; 30 pour le palier « +30 m³ ». */
export function paliersGrille(): number[] {
  return valeurDe('tarifs.garde_meubles').map((x) => x.volumeM3 ?? 30);
}

const PHRASE = /[^.!?]+[.!?]/g;

export function validerGardeMeublesUsages(aujourdhui: Date = new Date()): ProblemeUsage[] {
  const p: ProblemeUsage[] = [];
  const limite = new Date(aujourdhui);
  limite.setUTCFullYear(limite.getUTCFullYear() - 1);
  const perimee = (iso: string | null) => Boolean(iso) && new Date(iso as string) < limite;
  const paliers = new Set(paliersGrille());

  const slugs = PAGES_USAGES.map((x) => x.slug);
  for (const s of new Set(slugs.filter((x, i) => slugs.indexOf(x) !== i))) p.push({ ou: s, gravite: 'erreur', message: 'slug présent plusieurs fois' });
  const situations = PAGES_USAGES.map((x) => x.situation);
  for (const s of new Set(situations.filter((x, i) => situations.indexOf(x) !== i))) p.push({ ou: s, gravite: 'erreur', message: 'deux pages pour la même situation' });

  const questions = new Map<string, string>();
  const phrases = new Map<string, string>();

  for (const page of PAGES_USAGES) {
    const ou = `pages.${page.slug}`;
    if (page.verifiePar !== 'human' && page.verifiePar !== null) p.push({ ou, gravite: 'erreur', message: `verifiePar vaut « ${page.verifiePar} » — attendu "human" ou null` });
    if (page.publiable && !usageVerifie(page)) p.push({ ou, gravite: 'erreur', message: 'publiable=true mais page non relue' });
    if (page.verifiePar === null) p.push({ ou, gravite: 'avertissement', message: 'page collectée mais non relue (verifiePar: null) — marqueur « À VALIDER » rendu' });
    if (perimee(page.dateVerification)) p.push({ ou, gravite: 'avertissement', message: `page relue il y a plus de 12 mois (${page.dateVerification})` });

    if (page.titreMeta.length > 60) p.push({ ou, gravite: 'erreur', message: `titreMeta de ${page.titreMeta.length} caractères (max 60)` });
    if (page.descriptionMeta.length > 165) p.push({ ou, gravite: 'erreur', message: `descriptionMeta de ${page.descriptionMeta.length} caractères (max 165)` });
    const mots = page.capsule.split(/\s+/).length;
    if (mots < 40 || mots > 80) p.push({ ou, gravite: 'erreur', message: `capsule de ${mots} mots (viser 50 à 70)` });
    if (page.sections.length < 3) p.push({ ou, gravite: 'erreur', message: 'moins de trois sections' });
    if (page.faq.length < 3 || page.faq.length > 5) p.push({ ou, gravite: 'erreur', message: `faq : ${page.faq.length} question(s) — il en faut entre 3 et 5` });
    if (!page.servicePage.startsWith('/demenagement')) p.push({ ou, gravite: 'erreur', message: 'servicePage doit être une page de service déménagement' });
    if (!/Seraing/.test(page.capsule)) p.push({ ou, gravite: 'erreur', message: 'la capsule doit situer les box à Seraing' });

    for (const v of page.volumesCites) {
      if (!paliers.has(v)) p.push({ ou, gravite: 'erreur', message: `volume cité ${v} m³ absent de la grille tarifaire (paliers : ${[...paliers].join(', ')})` });
    }
    // Un volume écrit dans le texte qui n'est pas un palier : « 5 m³ », « 10 m³ ».
    const textes = [page.capsule, page.aRetenir, ...page.sections.map((s) => s.texte), ...page.faq.map((f) => f.a)];
    for (const t of textes) {
      for (const m of t.matchAll(/(\d+) m³/g)) {
        const v = Number(m[1]);
        if (!paliers.has(v) && v !== 5) p.push({ ou, gravite: 'erreur', message: `« ${m[0]} » n'est pas un palier de la grille` });
        if (v === 5) p.push({ ou, gravite: 'erreur', message: `« ${m[0]} » n'est pas un palier de la grille` });
      }
    }

    for (const f of page.faq) {
      const cle = f.q.trim().toLowerCase();
      const deja = questions.get(cle);
      if (deja) p.push({ ou, gravite: 'erreur', message: `question de FAQ déjà posée sur ${deja} : « ${f.q} »` });
      else questions.set(cle, page.slug);
    }
    // Différenciation : aucune phrase de plus de huit mots n'est partagée entre deux pages.
    for (const t of textes) {
      for (const ph of t.match(PHRASE) ?? []) {
        const cle = ph.trim().toLowerCase();
        if (cle.split(/\s+/).length < 8) continue;
        const deja = phrases.get(cle);
        if (deja && deja !== page.slug) p.push({ ou, gravite: 'erreur', message: `phrase déjà présente sur ${deja} : « ${ph.trim().slice(0, 60)}… »` });
        else phrases.set(cle, page.slug);
      }
    }
  }
  return p;
}
